import { useUserStore } from '../../store/useUserStore';
import { useAnalysisStore } from '../../store/useAnalysisStore';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Url from '../../data/url';
import { calculateAge } from '../../utility/utilities';
import { useTranslation } from '../../hook/useTranslation';
import {
  REPORT_STATUS,
  TALENT,
  ANALYSIS_STATUS,
  ON_KIDS_DRAFT_NAME,
  ON_KIDS_DRAFT_BIRTH,
  ON_KIDS_DRAFT_GENDER,
} from '../../data/define';
import ImgGroup from '../../assets/image/img_home_group.png';
import IcRightArrow from '../../assets/image/ic_chevron_right.svg';
import ImgFemale1 from '../../assets/image/profiles/img_kid_profile_female_1.png';
import OneBtnAlert from '../common/OneBtnAlert';
import TwoBtnAlert from '../common/TwoBtnAlert';
import { Report } from '../../types';

interface HeaderProps {
  reports?: (Report & { isSuccess?: boolean; updatedAt?: string; createdAt: string })[];
  setAnalysisToggleOpen: (open: boolean) => void;
}

interface DraftKid {
  name: string | null;
  birth: string | null;
  gender: string | null;
  imageUrl: string;
}

export default function Header({ reports = [], setAnalysisToggleOpen }: HeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useUserStore();
  const analysisState = useAnalysisStore((s) => s.analysisResult);
  const [analysisProgressAlertOpen, setAnalysisProgressAlertOpen] = useState(false);
  const [kidRegistrationAlertOpen, setKidRegistrationAlertOpen] = useState(false);
  const [draftKid, setDraftKid] = useState<DraftKid | null>(null);

  const guideTexts = {
    draft: {
      title: t('home.guide.draft.title'),
      description: '',
    },
    before: {
      title: t('home.guide.before.title'),
      description: t('home.guide.before.desc'),
    },
    completed: {
      title: t('home.guide.completed.title'),
      description: '',
    },
    inProgress: [
      {
        case: TALENT.RELATIONSHIP.key,
        title: t('home.guide.inProgress.relationship.title'),
        description: t('home.guide.inProgress.relationship.desc'),
      },
      {
        case: TALENT.LANGUAGE.key,
        title: t('home.guide.inProgress.language.title'),
        description: t('home.guide.inProgress.language.desc'),
      },
      {
        case: TALENT.MATH.key,
        title: t('home.guide.inProgress.math.title'),
        description: t('home.guide.inProgress.math.desc'),
      },
      {
        case: TALENT.PHYSICAL.key,
        title: t('home.guide.inProgress.physical.title'),
        description: t('home.guide.inProgress.physical.desc'),
      },
      {
        case: TALENT.REFLECTION.key,
        title: t('home.guide.inProgress.reflection.title'),
        description: t('home.guide.inProgress.reflection.desc'),
      },
      {
        case: TALENT.NATURE.key,
        title: t('home.guide.inProgress.nature.title'),
        description: t('home.guide.inProgress.nature.desc'),
      },
      {
        case: TALENT.SPACE.key,
        title: t('home.guide.inProgress.space.title'),
        description: t('home.guide.inProgress.space.desc'),
      },
      {
        case: TALENT.MUSIC.key,
        title: t('home.guide.inProgress.music.title'),
        description: t('home.guide.inProgress.music.desc'),
      },
    ],
  };

  const isDraftMode = !user.isLoggedIn;

  useEffect(() => {
    if (isDraftMode) {
      const draftName = localStorage.getItem(ON_KIDS_DRAFT_NAME);
      const draftBirth = localStorage.getItem(ON_KIDS_DRAFT_BIRTH);
      const draftGender = localStorage.getItem(ON_KIDS_DRAFT_GENDER);
      setDraftKid({
        name: draftName,
        birth: draftBirth,
        gender: draftGender,
        imageUrl: ImgFemale1,
      });
    }
  }, [isDraftMode]);

  let reportTextStatus: (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS] = REPORT_STATUS.BEFORE;
  if (!isDraftMode && reports?.length < 1) {
    reportTextStatus = REPORT_STATUS.BEFORE;
  } else if (!isDraftMode && !reports[0]?.isSuccess) {
    reportTextStatus = REPORT_STATUS.IN_PROGRESS;
  } else if (!isDraftMode && reports[0]?.isSuccess) {
    reportTextStatus = REPORT_STATUS.COMPLETED;
  } else if (isDraftMode) {
    reportTextStatus = REPORT_STATUS.DRAFT;
  }

  // 랜덤으로 하나 선택
  const inProgressRandomTalent =
    guideTexts.inProgress[Math.floor(Math.random() * guideTexts.inProgress.length)]; // 필요한 재능값 필요

  // 최근 완성 리포트 선택
  let latestCompletedReport:
    | (Report & { isSuccess?: boolean; updatedAt?: string; createdAt: string })
    | null = null;
  if (reports?.length > 0 && reports?.some((r) => r.isSuccess)) {
    latestCompletedReport = reports
      ?.filter((r) => r.isSuccess)
      ?.reduce((latest, current) => {
        return new Date(latest.createdAt).getTime() > new Date(current.createdAt).getTime()
          ? latest
          : current;
      });
  }

  const handleReportClick = () => {
    if (isDraftMode) {
      navigate(Url.Login);
      return;
    }
    if (reportTextStatus === REPORT_STATUS.BEFORE) {
      if (analysisState.status === ANALYSIS_STATUS.PENDING) {
        setAnalysisProgressAlertOpen(true);
        return;
      } else {
        if (!user?.kids || user?.kids?.length === 0 || !user?.mainKid) {
          setKidRegistrationAlertOpen(true);
        } else {
          setAnalysisToggleOpen(true);
        }
      }
    } else if (reportTextStatus === REPORT_STATUS.COMPLETED) {
      navigate(Url.Report);
    } else if (reportTextStatus === REPORT_STATUS.IN_PROGRESS) {
      navigate(Url.Report);
    }
  };

  return (
    <div
      className="bg-[#effcfc] pb-[24px]"
      style={{ width: '100vw', marginLeft: '-23px', marginRight: '-23px' }}
    >
      <div className="flex items-center justify-start w-full gap-3 py-[12px] px-[20px]">
        <div
          className="min-w-[64px] min-h-[64px] bg-[#FFF] overflow-hidden flex items-center justify-center rounded-[24px]"
          style={{ background: '#fff' }}
        >
          {user.isLoggedIn ? (
            user?.mainKid?.imageUrl ? (
              <img
                src={user?.mainKid?.imageUrl}
                alt="thumnail1"
                className="w-[64px] object-contain"
              />
            ) : (
              <img src={ImgFemale1} alt="thumnail1" className="object-contain w-[64px]" />
            )
          ) : (
            <img src={draftKid?.imageUrl} alt="thumnail1" className="object-contain w-[40px]" />
          )}
        </div>
        <div className="grow flex flex-col items-start gap-1 my-[12px]">
          <button
            type="button"
            className="flex items-center cursor-pointer gap-[5px]"
            onClick={() => {
              if (user.isLoggedIn) {
                if (user?.kids && user.kids.length < 1) {
                  if (!isDraftMode) navigate(Url.KidsRegister);
                } else {
                  if (!isDraftMode) navigate(Url.HomeChangeKids);
                }
              } else {
                navigate(Url.Login);
              }
            }}
          >
            <span className="text-sm font-[700] text-[20px]">
              {user.isLoggedIn ? (
                <>
                  {user?.kids && user?.kids?.length < 1
                    ? t('home.register_kid')
                    : user?.mainKid?.kidName}
                </>
              ) : (
                <>{draftKid?.name ?? t('home.register_kid')}</>
              )}
            </span>
            <img src={IcRightArrow} alt="right-icon" />
          </button>
          <div className="text-[14px] font-[400] text-gray-400">
            {user.isLoggedIn ? (
              <>
                {user?.kids && user?.kids?.length < 1 ? (
                  t('home.register_kid_desc')
                ) : (
                  <>
                    <span>{user?.mainKid?.gender === 'male' ? t('home.male') : t('home.female')}</span>•
                    {user?.mainKid?.kidBirth &&
                      t('common.age', { age: calculateAge(user?.mainKid?.kidBirth) })}
                  </>
                )}
              </>
            ) : (
              <>
                <span>
                  {draftKid?.gender === 'male' && t('home.male')}
                  {draftKid?.gender === 'female' && t('home.female')}
                </span>
                <span>
                  {draftKid?.birth ? (
                    <>•{t('common.age', { age: calculateAge(draftKid?.birth) })}</>
                  ) : (
                    <>{t('home.register_kid_desc')}</>
                  )}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="min-w-[50px] h-[150px] max-h-[150px] flex items-center justify-center rounded-xl">
        <img src={ImgGroup} alt="thumnail-group" />
      </div>
      <div className="w-full mt-[12px] mb-[8px] px-[20px]">
        <button
          type="button"
          className="p-[20px] rounded-[16px] bg-[#fff] w-full"
          onClick={handleReportClick}
        >
          <div className="w-full flex items-center justify-between mb-[8px]">
            <div className="text-[#1C1C1C] text-[14px] font-[700] text-left">
              {reportTextStatus === REPORT_STATUS.BEFORE && guideTexts.before.title}
              {reportTextStatus === REPORT_STATUS.COMPLETED && guideTexts.completed.title}
              {reportTextStatus === REPORT_STATUS.IN_PROGRESS && inProgressRandomTalent.title}
              {reportTextStatus === REPORT_STATUS.DRAFT && guideTexts.draft.title}
            </div>
            <img src={IcRightArrow} alt="right-icon" />
          </div>
          <div className="text-[#1C1C1C] text-[14px] font-[400] text-left">
            <span className="font-[700] text-[#5EBEBB]">
              {reportTextStatus === REPORT_STATUS.COMPLETED && t('home.completed_date')}
              {reportTextStatus !== REPORT_STATUS.COMPLETED &&
                reportTextStatus !== REPORT_STATUS.DRAFT &&
                t('home.tip')}
              {reportTextStatus === REPORT_STATUS.DRAFT && t('home.login_signup')}
            </span>
            &nbsp;
            <span>
              {reportTextStatus === REPORT_STATUS.BEFORE && guideTexts.before.description}
              {reportTextStatus === REPORT_STATUS.COMPLETED &&
                latestCompletedReport &&
                moment(latestCompletedReport?.updatedAt).format('YYYY.MM.DD')}
              {reportTextStatus === REPORT_STATUS.IN_PROGRESS && inProgressRandomTalent.description}
            </span>
          </div>
        </button>
        <OneBtnAlert
          open={analysisProgressAlertOpen}
          onClose={() => setAnalysisProgressAlertOpen(false)}
          title={t('nav.alert.analysis_in_progress.title')}
          description={t('nav.alert.analysis_in_progress.description')}
          btnText={t('common.confirm')}
          onClick={() => setAnalysisProgressAlertOpen(false)}
        />
        <TwoBtnAlert
          open={kidRegistrationAlertOpen}
          onClose={() => setKidRegistrationAlertOpen(false)}
          title={t('home.alert.register_kid.title')}
          content={t('home.alert.register_kid.description')}
          leftBtnText={t('common.cancel')}
          rightBtnText={t('common.confirm')}
          leftBtnClick={() => setKidRegistrationAlertOpen(false)}
          rightBtnClick={() => navigate(Url.KidsRegister)}
        />
      </div>
    </div>
  );
}
